 
"use client";

import React, { useEffect, useState, ReactNode } from 'react';

import { login, logout, LoginCredentials, AuthResponse } from '@lib/auth';

import { UserContext, UserContextType } from './user-context';

export interface FeatureFlags {
    financialModelVnext: boolean;
}

export interface User {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    state?: string;
    emailVerified: boolean;
    isStaff: boolean;
    isAdmin: boolean;
    created?: string;
    modified?: string;
    featureFlags?: FeatureFlags;
}



interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const handleLogin = async (credentials: LoginCredentials): Promise<AuthResponse> => {
        const result = await login(credentials);
        if (result.success) {
            await refreshUser();
        }
        return result;
    };

    const handleLogout = async (): Promise<AuthResponse> => {
        try {
            // Attempt server-side logout
            const result = await logout();
            
            // Always clear user data on client side regardless of server response
            setUser(null);
            
            // Clear any auth-related cookies (except CSRF which is needed for future requests)
            const cookies = document.cookie.split(';');
            for (const cookie of cookies) {
                const cookieName = cookie.split('=')[0].trim();
                if (cookieName !== 'csrftoken') {
                    // Expire the cookie
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                }
            }
            
            // Clear any auth data from localStorage if present
            localStorage.removeItem('user');
            localStorage.removeItem('authToken');
            
            return result;
        } catch {
            
            // Even if there's an error, still clear user data
            setUser(null);
            
            return {
                success: false,
                message: 'Error during logout process'
            };
        }
    };

    const fetchUser = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch('/api/account/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for authentication
            });

            if (response.status === 401 || response.status === 403) {
                // User is not authenticated
                setUser(null);

                return;
            }

            if (!response.ok) {
                throw new Error(`Failed to fetch user data: ${response.status}`);
            }

            const userData = await response.json();
            setUser(userData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch user data');
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const refreshUser = async () => {
        await fetchUser();
    };

    useEffect(() => {
        fetchUser();
    }, []);

    const value: UserContextType = {
        user,
        loading,
        error,
        refreshUser,
        isAuthenticated: user !== null,
        login: handleLogin,
        logout: handleLogout,
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};